import cls from 'classnames';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import { CSSProperties, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Markdown from 'react-markdown';

import _styles from './index.module.scss';
import {
  audioPlayIcon,
  audioLoadingIcon,
  audioReloadIcon,
  audioPlayingIcon,
  audioDisableIcon,
} from '../../../assets';
import imagePluginIcon from '../../../assets/Chat/image-plugin-icon.png';
import playCornerIcon from '../../../assets/Chat/play-corner.svg';
import imageFailed from '../../../assets/Plugin/image-failed.png';
import loadingIcon from '../../../assets/Plugin/loading.png';
import { useExposeItem } from '../../../hooks';
import { useEditPageTrack } from '../../../hooks/useEditPageTrack';
import { useAppSelector } from '../../../state';
import { Global_Image_Memory } from '../../../state/image';
import { track } from '../../../track';
import {
  IMessage,
  AudioStatus,
  MessageType,
  RoleType,
  ImgStatus,
  AttachmentImage,
} from '../../../types';
import { getNpcStatus } from '../../Home/NpcList';
import { LocalImage } from '../../Image';
import { CustomAudioPayload } from '../CustomAudio';
const Viewer = dynamic(() => import('react-viewer'), { ssr: false });

const PAGE = 'create_npc_advanced_page';

export interface IMessageProps {
  data: IMessage;
  isLast?: boolean;
  showVoiceDuration?: boolean;
  isMemory?: boolean;
  userAvatar?: string;
  npcAvatar?: string;
  fromWorkspace?: boolean;
  disableVoice?: boolean;
  onPlayAudio?: (payload: CustomAudioPayload) => void;
  onMessageRetry?: (payload: { mid: string }) => void;
  mdCls?: string;
  // 新增撤回相关 props
  showRecallButton?: boolean;
  onRecallMessage?: (payload: IMessage) => void;
}

type IMessagePropsWithAdapt = IMessageProps & {
  styles?: CSSProperties;
};

const WavIcon = ({ status }: { status: AudioStatus; styles?: CSSProperties }) => {
  switch (status) {
    case AudioStatus.Ready:
      return <img src={audioPlayIcon.src} className={_styles.icon} />;
    case AudioStatus.Reload:
      return <img src={audioReloadIcon.src} className={_styles.icon} />;
    case AudioStatus.Loading:
      return <img src={audioLoadingIcon.src} className={cls([_styles.icon, _styles.loading])} />;
    case AudioStatus.Disable:
      return <img src={audioDisableIcon.src} className={_styles.icon} />;
    case AudioStatus.Playing:
      return <img src={audioPlayingIcon.src} className={_styles.icon}></img>;
    default:
      return null;
  }
};

function MsgImage({ url }: { url: string }) {
  const file = Global_Image_Memory[url];
  const [imgUrl, setImgUrl] = useState('');
  useEffect(() => {
    if (typeof file !== 'object') return;
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = function (e) {
        typeof e.target?.result === 'string' && setImgUrl(e.target?.result);
      };
      reader.readAsDataURL(file); // 读取 file 并生成 Base64 格式
    }
  }, [file, url]);

  if (!imgUrl) return null;

  return <img src={imgUrl} className={_styles.atImg} />;
}

function GeneratedImage({ attachment }: { attachment: AttachmentImage }) {
  const maxWidth = 120;
  const initialSize = {
    width: maxWidth,
    height: maxWidth,
  };
  const [visible, setVisible] = useState(false);
  const { trackParams } = useEditPageTrack();

  if (attachment.height && attachment.width) {
    const scale = attachment.height / attachment.width;
    if (scale > 4 / 3) {
      initialSize.height = (initialSize.width * 4) / 3;
    }
    if (scale < 3 / 4) {
      initialSize.width = (initialSize.height * 3) / 4;
    }
  }

  return (
    <>
      <img
        src={attachment.url}
        className={_styles.generatedImg}
        style={{
          width: initialSize.width,
          height: initialSize.height,
        }}
        onClick={() => {
          track.send('ai_dialog_content_view', PAGE, {
            npc_id: trackParams.npc_id,
            ugc_npc_approving_status: trackParams.ugc_npc_approving_status,
            dialog_model_choice: trackParams.dialog_model_choice,
            photo_url: attachment.url,
          });
          setVisible(true);
        }}
      />
      <Viewer
        visible={visible}
        onClose={() => {
          setVisible(false);
        }}
        images={[{ src: attachment.url, alt: '' }]}
        rotatable={false}
        scalable={false}
        downloadable={false}
        noImgDetails
        changeable={false}
        disableKeyboardSupport
        showTotal={false}
        noNavbar
        noClose
        onMaskClick={() => {
          setVisible(false);
        }}
      />
    </>
  );
}

function GeneratedLoading() {
  return (
    <div className={_styles.imgLoading}>
      <LocalImage src={loadingIcon} />
    </div>
  );
}

function AIImageAttachmentMsg({
  attachment,
  isDiscarded,
}: {
  attachment: AttachmentImage;
  isDiscarded: boolean;
}) {
  const { t } = useTranslation();
  const [isTimeout, setIsTimeout] = useState(false);
  const content = useMemo(() => {
    if (attachment.status === ImgStatus.Generating && !isTimeout) return <GeneratedLoading />;
    if (attachment.status === ImgStatus.Success && !isTimeout)
      return <GeneratedImage attachment={attachment} />;
    return (
      <div className={_styles.imgLoading} style={{ backgroundImage: `url(${imageFailed.src})` }} />
    );
  }, [attachment, isTimeout]);

  const timeoutId = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (attachment.status !== ImgStatus.Generating) {
      timeoutId.current && clearTimeout(timeoutId.current);
      setIsTimeout(false);
      return;
    }
    timeoutId.current = setTimeout(() => {
      setIsTimeout(true);
    }, 60000);
    return () => {
      timeoutId.current && clearTimeout(timeoutId.current);
    };
  }, [attachment.status]);

  return (
    <div className={cls(_styles.aiImageMessage, isDiscarded ? _styles.discarded : '')}>
      <div className={_styles.pluginInfo}>
        <LocalImage src={imagePluginIcon} />
        <span>{t('img_plugin_name')}</span>
      </div>
      {content}
    </div>
  );
}

const MsgContent: FC<IMessagePropsWithAdapt> = ({
  styles,
  data,
  showVoiceDuration = true,
  fromWorkspace,
  disableVoice,
  onPlayAudio = () => {},
  onMessageRetry = () => {},
  mdCls,
  showRecallButton = false,
  onRecallMessage = () => {},
}) => {
  const {
    mid,
    txt,
    text_info,
    type,
    role_type,
    wav_status,
    voice_uri,
    voice_duration_ms,
    discarded,
    attachment,
    npc_id,
    custom_mid,
    is_open,
    is_ready,
  } = data;

  const {
    statusInfo,
    settingInfo: { chat_model_type },
  } = useAppSelector((state) => state.edit);

  const onClickAsideSchema = (schema: string) => {
    console.log('onClickAsideSchema', schema);
    if (discarded) return;
    // 临时兼容重试旁边处理
    if (schema === 'aside://message_retry') {
      // 重发消息
      onMessageRetry({ mid });
    }
  };

  const onClickPlayAudio = (payload: CustomAudioPayload) => {
    if (discarded || disableVoice) return;

    track.send('voice_chat_play', PAGE, {
      npc_id: payload.npcId,
      ugc_npc_approving_status: getNpcStatus(statusInfo),
      message_id: payload.mid,
      dialog_model_choice: chat_model_type,
      is_open: payload.isOepn,
    });
    onPlayAudio && onPlayAudio(payload);
  };

  const handleRecallMessage = () => {
    onRecallMessage(data);
  };

  const isText = type === MessageType.Text;
  const isAI = role_type === RoleType.AI;

  return (
    <>
      {isText ? (
        <div className={_styles.messageContainer}>
          {/* 撤回按钮 */}
          {showRecallButton && (
            <div className={_styles.afresh} onClick={() => handleRecallMessage()}>
              <img
                src={
                  isAI
                    ? 'https://talkie.cdn.minimax.io/public-cdn-s3-us-west-2/talkie-op-img/image/1390598166_1735191816207_Rewind_line_24.svg'
                    : 'https://talkie.cdn.minimax.io/public-cdn-s3-us-west-2/talkie-op-img/image/336155020_1735191816425_Recall_line_24.svg'
                }
                className={_styles.redo}
                alt="ai character resend message icon"
              />
            </div>
          )}
          <div
            id={String(mid)}
            className={cls([
              _styles.message,
              isAI ? _styles.aiMessage : _styles.userMessage,
              discarded ? _styles.discarded : '',
            ])}
          >
            {isAI ? (
              <div
                className={cls([
                  _styles.audio,
                  !voice_duration_ms && showVoiceDuration ? _styles.noPointer : '',
                ])}
                onClick={() =>
                  onClickPlayAudio({
                    url: voice_uri ?? '',
                    mid,
                    npcId: npc_id,
                    status: wav_status,
                    isOepn: is_open,
                    fromWorkspace,
                    customMid: custom_mid,
                  })
                }
              >
                <LocalImage
                  src={playCornerIcon}
                  className={_styles.corner}
                  alt="play ai chatbot voice corner"
                />
                {disableVoice ? (
                  <WavIcon styles={styles} status={AudioStatus.Disable} />
                ) : is_ready || voice_duration_ms || is_open ? (
                  <>
                    <WavIcon styles={styles} status={wav_status as AudioStatus} />
                    {showVoiceDuration ? <span>{voice_duration_ms}″</span> : null}
                  </>
                ) : (
                  <WavIcon styles={styles} status={AudioStatus.Ready} />
                )}
              </div>
            ) : null}
            <div className={_styles.text}>
              <Markdown
                className={mdCls}
                components={{
                  em: (node) => <span className={_styles.em}>{node.children}</span>,
                  a: (node) => (
                    <a href={node.href} rel={'nofollow'}>
                      {node.children}
                    </a>
                  ),
                }}
              >
                {txt}
              </Markdown>
            </div>
          </div>
        </div>
      ) : null}
      {type === MessageType.Aside ? (
        <div
          id={String(mid)}
          className={cls([_styles.asideMessage, discarded ? _styles.discarded : ''])}
        >
          <div className={_styles.txt}>
            {text_info ? (
              text_info?.map((item, index) => {
                const { text, schema } = item;
                if (item.schema) {
                  return (
                    <span
                      key={`${index}_${text}`}
                      className={_styles.schema}
                      onClick={() => {
                        onClickAsideSchema(schema);
                      }}
                    >
                      {text}
                    </span>
                  );
                }
                return <span key={`${index}_${text}`}>{text}</span>;
              })
            ) : txt ? (
              <span>{txt}</span>
            ) : null}
          </div>
        </div>
      ) : null}
      {type === MessageType.Reset ? (
        <div id={String(mid)} className={_styles.resetMessage}>
          <div className={_styles.split}></div>
          <div className={_styles.txt}>{txt}</div>
          <div className={_styles.split}></div>
        </div>
      ) : null}
      {isAI && type === MessageType.Attachment && !!attachment ? (
        <AIImageAttachmentMsg attachment={attachment} isDiscarded={!!discarded} />
      ) : null}
      {!isAI && type === MessageType.Attachment && attachment?.url ? (
        <div
          id={String(mid)}
          className={cls([
            _styles.message,
            isAI ? _styles.aiMessage : _styles.userMessage,
            discarded ? _styles.discarded : '',
          ])}
        >
          <MsgImage url={attachment.url} />
          <div className={_styles.text}>
            <Markdown
              className={mdCls}
              components={{
                em: (node) => <span className={_styles.em}>{node.children}</span>,
                a: (node) => (
                  <a href={node.href} rel={'nofollow'}>
                    {node.children}
                  </a>
                ),
              }}
            >
              {txt}
            </Markdown>
          </div>
        </div>
      ) : null}
    </>
  );
};

export const EMessageTypeTrackText = {
  [MessageType.Text]: 'text',
  [MessageType.Attachment]: 'pic',
  [MessageType.Reset]: 'reset',
  [MessageType.Aside]: 'aside',
  [MessageType.Intro]: 'intro',
  [MessageType.Voice]: 'voice',
};

export const MessageItem: FC<IMessagePropsWithAdapt> = (props) => {
  const { data } = props;
  const { role_type } = data;
  const isAI = role_type === RoleType.AI;

  const ref = useRef<HTMLDivElement>(null);
  const { trackParams } = useEditPageTrack();
  const onExpose = useCallback(() => {
    track.send('ai_dialog_content_view', PAGE, {
      npc_id: trackParams.npc_id,
      message_id: data.mid,
      message_type: EMessageTypeTrackText[data.type],
      dialog_model_choice: trackParams.dialog_model_choice,
      message_sender: isAI ? 'ai' : 'user',
    });
  }, [data.mid]);

  useExposeItem({
    key: data.mid,
    ref,
    onExpose,
  });

  return (
    <div
      ref={ref}
      className={cls([_styles.messageWrap], {
        [_styles.user]: !isAI,
      })}
    >
      <MsgContent {...props} />
    </div>
  );
};
